import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { RefreshCcw } from "lucide-react";

const statusOptions = [
  { value: "all", label: "Any Status" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
];

interface JourneyLog {
  id: number;
  recipient: string;
  message: string;
  status: string | null;
  reference: string | null;
  templateId: number | null;
  sentAt: string | null;
  cost: string | null;
  remainingBalance: string | null;
  journeyId: number | null;
  journeyName: string | null;
  userId: number | null;
  userEmail: string | null;
  userName: string | null;
}

interface JourneyLogSummary {
  totalCreditsUsed: number;
}

interface JourneyLogResponse {
  success: boolean;
  data: JourneyLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  summary?: JourneyLogSummary;
}

export default function JourneySMSTrackingTab() {
  const [filters, setFilters] = useState({
    journeyId: "",
    userId: "",
    status: "",
    recipient: "",
    startDate: "",
    endDate: "",
  });
  const [page, setPage] = useState(1);
  const limit = 25;

  const updateFilters = (updates: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(1);
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    params.set("page", page.toString());
    params.set("limit", limit.toString());
    return params.toString();
  }, [filters, page]);

  const { data, isLoading, isFetching, refetch } = useQuery<JourneyLogResponse>({
    queryKey: ["/api/sms-config/logs/journeys", queryString],
    queryFn: async () => {
      const url = queryString
        ? `/api/sms-config/logs/journeys?${queryString}`
        : "/api/sms-config/logs/journeys";
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const logs = data?.data ?? [];
  const summary = data?.summary;

  const creditsUsed = summary?.totalCreditsUsed ?? data?.pagination?.total ?? 0;
  const showSummary = summary || data?.pagination;

  const formatCredits = (value?: string | number | null) => {
    const numeric = Number(value ?? 0);
    if (Number.isNaN(numeric)) {
      return "0.00";
    }
    return numeric.toFixed(2);
  };

  const formatDisplayCredits = (value?: string | number | null) => `${formatCredits(value)} credits`;
  const perLogCreditLabel = formatDisplayCredits(1);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle>Journey SMS Tracking</CardTitle>
          <CardDescription>Review SMS sends generated by automated user journeys.</CardDescription>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching || isLoading}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder="Journey ID"
            value={filters.journeyId}
            onChange={(event) => updateFilters({ journeyId: event.target.value })}
          />
          <Input
            placeholder="User ID"
            value={filters.userId}
            onChange={(event) => updateFilters({ userId: event.target.value })}
          />
          <Input
            placeholder="Recipient phone"
            value={filters.recipient}
            onChange={(event) => updateFilters({ recipient: event.target.value })}
          />
          <Select
            value={filters.status || "all"}
            onValueChange={(value) =>
              updateFilters({ status: value === "all" ? "" : value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={filters.startDate}
            onChange={(event) => updateFilters({ startDate: event.target.value })}
          />
          <Input
            type="date"
            value={filters.endDate}
            onChange={(event) => updateFilters({ endDate: event.target.value })}
          />
          <Button
            variant="secondary"
            onClick={() =>
              updateFilters({
                journeyId: "",
                userId: "",
                status: "",
                recipient: "",
                startDate: "",
                endDate: "",
              })
            }
          >
            Clear Filters
          </Button>
        </div>

        {showSummary && (
          <div className="rounded-md border border-dashed border-muted p-4 text-sm text-muted-foreground md:flex md:items-center md:justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Credits used</div>
              <div className="text-lg font-semibold text-slate-900">{formatDisplayCredits(creditsUsed)}</div>
            </div>
            <div className="mt-3 md:mt-0">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Total logs</div>
              <div className="text-lg font-semibold text-slate-900">{data?.pagination?.total ?? 0}</div>
            </div>
          </div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Journey</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Sent At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    Loading journey SMS logs...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    No journey SMS logs found for the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.journeyName ? (
                        <div>
                          <div className="font-medium">{log.journeyName}</div>
                          <div className="text-xs text-muted-foreground">Journey #{log.journeyId}</div>
                        </div>
                      ) : (
                        <div>Journey #{log.journeyId ?? "unknown"}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.userEmail ? (
                        <div>
                          <div className="font-medium">{log.userEmail}</div>
                          <div className="text-xs text-muted-foreground">User #{log.userId}</div>
                        </div>
                      ) : (
                        <div>User #{log.userId ?? "unknown"}</div>
                      )}
                    </TableCell>
                    <TableCell>{log.recipient || "N/A"}</TableCell>
                    <TableCell>
                      <Badge variant={log.status === "failed" ? "destructive" : "secondary"}>
                        {log.status || "unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {perLogCreditLabel}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.remainingBalance
                        ? `${formatCredits(log.remainingBalance)} credits`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.sentAt ? new Date(log.sentAt).toLocaleString() : "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {data?.pagination && (
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-sm text-muted-foreground">
            <div>
              Showing {(data.pagination.page - 1) * limit + 1}-
              {Math.min(data.pagination.page * limit, data.pagination.total)} of {data.pagination.total} logs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={data.pagination.page === 1}
              >
                Previous
              </Button>
              <span>
                Page {data.pagination.page} of {Math.max(data.pagination.pages, 1)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((prev) => Math.min(data.pagination.pages, prev + 1))}
                disabled={data.pagination.page >= data.pagination.pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
